import { cn } from "@/lib/utils";

// Semi-circular gauge, 0-10 scale
export const Gauge = ({ value, testid }) => {
  const pct = Math.max(0, Math.min(1, (value ?? 0) / 10));
  const r = 46;
  const len = Math.PI * r;
  return (
    <svg width="120" height="72" viewBox="0 0 120 72" data-testid={testid}>
      <defs>
        <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(352 72% 50%)" />
          <stop offset="48%" stopColor="hsl(40 94% 52%)" />
          <stop offset="100%" stopColor="hsl(154 62% 36%)" />
        </linearGradient>
      </defs>
      <path d={`M 14 60 A ${r} ${r} 0 0 1 106 60`} fill="none" stroke="url(#gaugeGrad)" strokeWidth="11" strokeLinecap="round" />
      <path
        d={`M 14 60 A ${r} ${r} 0 0 1 106 60`}
        fill="none"
        stroke="hsl(var(--muted))"
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={len}
        strokeDashoffset={len * pct}
        opacity="0.75"
      />
      <g transform={`rotate(${-90 + pct * 180} 60 60)`}>
        <line x1="60" y1="60" x2="60" y2="22" stroke="hsl(var(--foreground))" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <circle cx="60" cy="60" r="4.5" fill="hsl(var(--foreground))" />
    </svg>
  );
};

// Circular ring score, 0-10 scale
export const Ring = ({ value, label, tone = "stroke-deepblue", testid, children }) => {
  const pct = Math.max(0, Math.min(1, (value ?? 0) / 10));
  const c = 2 * Math.PI * 34;
  return (
    <div className="relative grid h-28 w-28 place-items-center" data-testid={testid}>
      <svg width="112" height="112" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r="34" fill="none" stroke="hsl(var(--muted))" strokeWidth="7" />
        <circle
          cx="48"
          cy="48"
          r="34"
          fill="none"
          className={tone}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{
            filter: "drop-shadow(0 2px 8px hsl(44 68% 50% / 0.45))",
            transition: "stroke-dashoffset 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </svg>
      <div className="absolute grid place-items-center text-center">
        {children || <span className="font-mono text-base tabular-nums text-navy">{label}</span>}
      </div>
    </div>
  );
};

export const ScoreBar = ({ value, className }) => (
  <div className="mt-2 h-[7px] w-full overflow-hidden rounded-full bg-muted">
    <div
      className={cn("h-full rounded-full", className)}
      style={{
        width: `${Math.max(0, Math.min(100, ((value ?? 0) / 10) * 100))}%`,
        transition: "width 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    />
  </div>
);

export const IndicativeNote = ({ testid }) => (
  <div
    data-testid={testid}
    className="panel border-l-[3px] border-l-gold px-6 py-5"
  >
    <p className="font-display text-xl text-navy">
      How effectively would you like to review your team's performance?
    </p>
    <p className="mt-3 text-base text-foreground/85">
      <span className="font-display text-lg text-goldink">90 minutes daily</span> → Review, respond and stay in control.
    </p>
    <p className="mt-1.5 text-base text-foreground/85">
      <span className="font-display text-lg text-goldink">2 focused days monthly</span> → Reflect, plan and align.
    </p>
  </div>
);
